import type { GameId } from '@tira/protocol';
import type { JSX } from 'react';

/**
 * The six stations of the compound, drawn as flat SVG dioramas — design spec
 * §4. Each one is a real place with a real light source, not an icon: the
 * carport bulb, the sari-sari tube, the string lights, the balcony city-glow,
 * the sodium-vapour streetlight, the tanod desk lamp.
 *
 * `currentColor` carries the venue accent, so the station sets it once and the
 * signage, tube or lamp glass picks it up (accents are light, never fills).
 * Geometry is deliberately cheap: no filters, no masks, no bitmaps — this whole
 * strip has to glide on a Redmi-class phone.
 */

const VIEW = '0 0 320 200';

/** Warm tungsten, cool fluorescent — the two-source language, as gradient stops. */
function Lights({ id }: { id: string }): JSX.Element {
  return (
    <defs>
      <radialGradient id={`${id}-warm`} cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor="#ffb35c" stopOpacity="0.55" />
        <stop offset="60%" stopColor="#ffb35c" stopOpacity="0.14" />
        <stop offset="100%" stopColor="#ffb35c" stopOpacity="0" />
      </radialGradient>
      <radialGradient id={`${id}-cool`} cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor="#cde8ff" stopOpacity="0.4" />
        <stop offset="100%" stopColor="#cde8ff" stopOpacity="0" />
      </radialGradient>
      <linearGradient id={`${id}-cone`} x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#ffb35c" stopOpacity="0.34" />
        <stop offset="100%" stopColor="#ffb35c" stopOpacity="0" />
      </linearGradient>
      <linearGradient id={`${id}-wood`} x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#5b3f2a" />
        <stop offset="100%" stopColor="#2b1d13" />
      </linearGradient>
    </defs>
  );
}

const NIGHT = '#0a0e15';
const DARK = '#131924';
const EDGE = 'rgba(232,217,176,.16)';

export function StationArt({ game }: { game: GameId }): JSX.Element {
  switch (game) {
    case 'mahjong':
      return <Carport />;
    case 'tongits':
      return <SariSari />;
    case 'pusoydos':
      return <Garahe />;
    case 'pusoy':
      return <Balkonahe />;
    case 'dama':
      return <Bangketa />;
    case 'gera':
      return <Outpost />;
  }
}

/** G1 — the carport: canopy, tungsten bulb, monobloc chairs, electric fan. */
function Carport(): JSX.Element {
  return (
    <svg viewBox={VIEW} className="station__art" role="presentation">
      <Lights id="mj" />
      <ellipse cx="160" cy="168" rx="118" ry="34" fill="url(#mj-warm)" />
      <polygon points="150,66 170,66 214,168 106,168" fill="url(#mj-cone)" />

      {/* canopy slab on two posts */}
      <path d="M28 44h264l-10 14H38z" fill={DARK} />
      <path d="M28 44h264" stroke={EDGE} strokeWidth="1" />
      <rect x="40" y="58" width="6" height="112" fill={NIGHT} />
      <rect x="274" y="58" width="6" height="112" fill={NIGHT} />

      {/* bulb on its cord */}
      <line x1="160" y1="58" x2="160" y2="72" stroke="rgba(232,217,176,.3)" strokeWidth="1" />
      <circle cx="160" cy="77" r="6" fill="#ffdca6" />
      <circle cx="160" cy="77" r="14" fill="url(#mj-warm)" />

      {/* the serious table + a rack of tiles */}
      <rect x="98" y="122" width="124" height="34" rx="5" fill="url(#mj-wood)" />
      <rect x="98" y="122" width="124" height="6" rx="3" fill="currentColor" opacity=".5" />
      <g fill="#e8e2d2">
        {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
          <rect key={i} x={110 + i * 12} y="132" width="8" height="14" rx="1.5" />
        ))}
      </g>
      <rect x="120" y="156" width="8" height="16" fill={NIGHT} />
      <rect x="192" y="156" width="8" height="16" fill={NIGHT} />

      {/* monobloc chairs, backs to us */}
      {[64, 236].map((x) => (
        <g key={x} fill={NIGHT}>
          <rect x={x} y="118" width="26" height="6" rx="3" />
          <rect x={x + 2} y="94" width="22" height="26" rx="5" />
          <rect x={x + 4} y="124" width="4" height="22" />
          <rect x={x + 18} y="124" width="4" height="22" />
        </g>
      ))}

      {/* electric fan in the corner, doing what it can */}
      <g fill={NIGHT} stroke={EDGE} strokeWidth="1">
        <circle cx="290" cy="120" r="15" fill={NIGHT} />
        <path d="M290 108v24M279 120h22" stroke="rgba(205,232,255,.25)" />
        <rect x="288" y="135" width="4" height="26" />
        <rect x="280" y="161" width="20" height="4" rx="2" />
      </g>
    </svg>
  );
}

/** G2 — the sari-sari bench: chicken-wire grille, sachet strips, one tube. */
function SariSari(): JSX.Element {
  return (
    <svg viewBox={VIEW} className="station__art" role="presentation">
      <Lights id="tg" />
      <ellipse cx="150" cy="172" rx="120" ry="30" fill="url(#tg-cool)" opacity=".7" />

      {/* store front */}
      <rect x="30" y="30" width="180" height="126" fill={DARK} />
      <path d="M22 30h196l-8 16H30z" fill={NIGHT} />
      <rect x="46" y="56" width="148" height="62" fill="#0d121b" />

      {/* chicken-wire grille */}
      <g stroke="rgba(232,217,176,.22)" strokeWidth="0.7">
        {Array.from({ length: 9 }, (_, i) => (
          <line key={`v${i}`} x1={46 + i * 18.5} y1="56" x2={46 + i * 18.5} y2="118" />
        ))}
        {Array.from({ length: 5 }, (_, i) => (
          <line key={`h${i}`} x1="46" y1={56 + i * 15.5} x2="194" y2={56 + i * 15.5} />
        ))}
      </g>

      {/* one fluorescent tube, the only light that matters here */}
      <rect x="52" y="48" width="136" height="5" rx="2.5" fill="#e9f4ff" />
      <rect x="40" y="40" width="160" height="22" rx="8" fill="url(#tg-cool)" />

      {/* hanging sachet strips */}
      <g>
        {[70, 92, 114, 136, 158, 180].map((x, i) => (
          <g key={x}>
            <line x1={x} y1="46" x2={x} y2={78 + (i % 3) * 8} stroke="rgba(232,217,176,.35)" strokeWidth="1" />
            {[0, 1, 2].map((j) => (
              <rect
                key={j}
                x={x - 4}
                y={52 + j * 9}
                width="8"
                height="7"
                rx="1"
                fill="currentColor"
                opacity={0.75 - j * 0.15}
              />
            ))}
          </g>
        ))}
      </g>

      {/* counter, bench, small table — exactly three seats fit */}
      <rect x="30" y="118" width="180" height="10" fill="url(#tg-wood)" />
      <rect x="60" y="146" width="130" height="9" rx="3" fill="url(#tg-wood)" />
      <rect x="66" y="155" width="6" height="18" fill={NIGHT} />
      <rect x="178" y="155" width="6" height="18" fill={NIGHT} />
      <rect x="222" y="132" width="60" height="8" rx="3" fill="url(#tg-wood)" />
      <rect x="228" y="140" width="5" height="30" fill={NIGHT} />
      <rect x="271" y="140" width="5" height="30" fill={NIGHT} />
      <g fill="#e8e2d2" opacity=".9">
        <rect x="236" y="126" width="12" height="8" rx="1.5" transform="rotate(-8 242 130)" />
        <rect x="252" y="126" width="12" height="8" rx="1.5" transform="rotate(6 258 130)" />
      </g>
    </svg>
  );
}

/** G3 — the garahe: rolled-up door, string lights, plastic table, cooler box. */
function Garahe(): JSX.Element {
  return (
    <svg viewBox={VIEW} className="station__art" role="presentation">
      <Lights id="pd" />
      <ellipse cx="160" cy="170" rx="126" ry="32" fill="url(#pd-warm)" opacity=".85" />

      <rect x="34" y="24" width="252" height="146" fill="#0b1017" />
      <rect x="34" y="24" width="252" height="30" fill={DARK} />
      <g stroke="rgba(232,217,176,.12)" strokeWidth="1">
        {[30, 36, 42, 48].map((y) => (
          <line key={y} x1="38" y1={y} x2="282" y2={y} />
        ))}
      </g>

      {/* string lights sagging across the opening */}
      <path d="M40 62q120 40 240 0" fill="none" stroke="rgba(232,217,176,.28)" strokeWidth="1" />
      {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => {
        const t = i / 7;
        const x = 40 + t * 240;
        const y = 62 + 4 * 10 * (t - t * t) * 2;
        return (
          <g key={i}>
            <circle cx={x} cy={y + 5} r="3.4" fill="#ffd9a1" />
            <circle cx={x} cy={y + 5} r="11" fill="url(#pd-warm)" />
          </g>
        );
      })}

      {/* plastic table for four */}
      <rect x="104" y="120" width="112" height="12" rx="6" fill="#3a4250" />
      <rect x="104" y="120" width="112" height="4" rx="2" fill="currentColor" opacity=".55" />
      <rect x="116" y="132" width="6" height="30" fill={NIGHT} />
      <rect x="198" y="132" width="6" height="30" fill={NIGHT} />
      <g fill="#e8e2d2" opacity=".92">
        <rect x="140" y="112" width="14" height="10" rx="1.5" transform="rotate(-10 147 117)" />
        <rect x="158" y="112" width="14" height="10" rx="1.5" transform="rotate(9 165 117)" />
      </g>

      {/* cooler box doing duty as the fourth seat's side table */}
      <rect x="238" y="128" width="46" height="34" rx="4" fill="#2b3442" />
      <rect x="236" y="124" width="50" height="8" rx="3" fill="#39445a" />
      <rect x="246" y="140" width="30" height="4" rx="2" fill="currentColor" opacity=".5" />

      {/* two monobloc chairs */}
      {[56, 90].map((x) => (
        <g key={x} fill={NIGHT}>
          <rect x={x} y="122" width="24" height="6" rx="3" />
          <rect x={x + 2} y="100" width="20" height="24" rx="5" />
          <rect x={x + 3} y="128" width="4" height="26" />
          <rect x={x + 17} y="128" width="4" height="26" />
        </g>
      ))}
    </svg>
  );
}

/** G4 — the balcony: veranda railing, quiet table, city glow on the horizon. */
function Balkonahe(): JSX.Element {
  return (
    <svg viewBox={VIEW} className="station__art" role="presentation">
      <Lights id="ps" />

      {/* city-glow horizon, far below and far away */}
      <rect x="0" y="86" width="320" height="30" fill="url(#ps-cool)" opacity=".5" />
      <g fill="#0a0e15">
        {[10, 42, 74, 108, 146, 182, 214, 250, 286].map((x, i) => (
          <rect key={x} x={x} y={70 + (i % 4) * 8} width={22 + (i % 3) * 6} height="46" />
        ))}
      </g>
      <g fill="#ffd9a1" opacity=".5">
        {Array.from({ length: 26 }, (_, i) => (
          <rect key={i} x={14 + (i * 23) % 292} y={78 + ((i * 13) % 30)} width="2" height="2" />
        ))}
      </g>

      <rect x="0" y="116" width="320" height="6" fill={DARK} />

      {/* balusters */}
      <g fill={NIGHT}>
        <rect x="0" y="112" width="320" height="5" rx="2" />
        {Array.from({ length: 15 }, (_, i) => (
          <rect key={i} x={8 + i * 21} y="117" width="6" height="26" rx="2" />
        ))}
        <rect x="0" y="142" width="320" height="6" />
      </g>

      {/* one soft lamp over a table set for a slow game */}
      <line x1="196" y1="24" x2="196" y2="46" stroke="rgba(232,217,176,.25)" strokeWidth="1" />
      <path d="M182 46h28l-6 12h-16z" fill={DARK} />
      <circle cx="196" cy="60" r="16" fill="url(#ps-warm)" />
      <ellipse cx="176" cy="164" rx="96" ry="26" fill="url(#ps-warm)" opacity=".7" />

      <rect x="120" y="146" width="112" height="11" rx="5" fill="url(#ps-wood)" />
      <rect x="120" y="146" width="112" height="4" rx="2" fill="currentColor" opacity=".5" />
      <rect x="132" y="157" width="6" height="24" fill={NIGHT} />
      <rect x="214" y="157" width="6" height="24" fill={NIGHT} />
      <g fill="#e8e2d2" opacity=".9">
        <rect x="150" y="138" width="13" height="9" rx="1.5" />
        <rect x="166" y="138" width="13" height="9" rx="1.5" />
        <rect x="182" y="138" width="13" height="9" rx="1.5" />
      </g>
    </svg>
  );
}

/**
 * G5 — the sidewalk. The most iconic image in the app (§4): painted plywood on
 * a low table, tansan caps for pieces, sodium-vapour streetlight overhead.
 */
function Bangketa(): JSX.Element {
  const caps: { x: number; y: number; mine: boolean }[] = [
    { x: 122, y: 132, mine: false },
    { x: 146, y: 132, mine: false },
    { x: 170, y: 132, mine: false },
    { x: 134, y: 144, mine: false },
    { x: 182, y: 144, mine: true },
    { x: 126, y: 158, mine: true },
    { x: 158, y: 158, mine: true },
    { x: 194, y: 158, mine: true },
  ];
  return (
    <svg viewBox={VIEW} className="station__art" role="presentation">
      <Lights id="dm" />

      {/* streetlight: pole, hood, and the hard cone it throws */}
      <rect x="252" y="18" width="4" height="150" fill="#1a2130" />
      <path d="M254 22h34" stroke="#1a2130" strokeWidth="4" />
      <path d="M282 24h14l-3 8h-8z" fill="#232c3c" />
      <circle cx="289" cy="34" r="5" fill="#ffd9a1" />
      <polygon points="283,36 295,36 340,180 238,180" fill="url(#dm-cone)" />
      <ellipse cx="176" cy="176" rx="132" ry="30" fill="url(#dm-warm)" />

      {/* kerb + a strip of street */}
      <rect x="0" y="168" width="320" height="4" fill="#242c39" />
      <rect x="0" y="172" width="320" height="28" fill="#0d1219" />

      {/* the board: painted plywood, seen at a slight angle */}
      <polygon points="104,118 216,118 236,172 84,172" fill="#8a6a3f" />
      <polygon points="108,121 212,121 230,169 90,169" fill="#c9a86c" />
      <g stroke="#4c3a22" strokeWidth="1.1" opacity=".85">
        {Array.from({ length: 7 }, (_, i) => {
          const t = (i + 1) / 8;
          return (
            <line
              key={`c${i}`}
              x1={108 + t * 104}
              y1="121"
              x2={90 + t * 140}
              y2="169"
              vectorEffect="non-scaling-stroke"
            />
          );
        })}
        {Array.from({ length: 7 }, (_, i) => {
          const t = (i + 1) / 8;
          const yy = 121 + t * 48;
          const x1 = 108 - t * 18;
          const x2 = 212 + t * 18;
          return <line key={`r${i}`} x1={x1} y1={yy} x2={x2} y2={yy} vectorEffect="non-scaling-stroke" />;
        })}
      </g>
      {/* painted dark squares, thinned to a few so the grid still reads */}
      <g fill="#4c3a22" opacity=".55">
        <polygon points="121,121 134,121 132,133 118,133" />
        <polygon points="147,121 160,121 159,133 145,133" />
        <polygon points="173,121 186,121 186,133 172,133" />
        <polygon points="112,145 128,145 125,158 108,158" />
        <polygon points="144,145 160,145 159,158 141,158" />
        <polygon points="176,145 192,145 193,158 175,158" />
      </g>

      {/* tansan caps — gold for one side, dark for the other, crimped edges */}
      {caps.map((c) => (
        <g key={`${c.x}-${c.y}`}>
          <ellipse cx={c.x} cy={c.y + 2} rx="7" ry="3.4" fill="#000" opacity=".45" />
          <ellipse
            cx={c.x}
            cy={c.y}
            rx="7.4"
            ry="4"
            fill={c.mine ? '#c9a227' : '#1c222d'}
            stroke={c.mine ? '#7d6414' : '#39424f'}
            strokeWidth="1"
          />
          <ellipse cx={c.x} cy={c.y - 0.6} rx="4" ry="2" fill={c.mine ? '#e6c76a' : '#2b3341'} opacity=".9" />
        </g>
      ))}

      {/* two stools, one each side, nobody sitting yet */}
      <g fill={NIGHT}>
        <rect x="44" y="134" width="30" height="6" rx="3" />
        <rect x="48" y="140" width="5" height="28" />
        <rect x="66" y="140" width="5" height="28" />
        <rect x="246" y="130" width="30" height="6" rx="3" />
        <rect x="250" y="136" width="5" height="30" />
        <rect x="268" y="136" width="5" height="30" />
      </g>
    </svg>
  );
}

/** G6 — the barangay outpost: tanod desk, two chairs facing off, one lamp. */
function Outpost(): JSX.Element {
  return (
    <svg viewBox={VIEW} className="station__art" role="presentation">
      <Lights id="gr" />
      <ellipse cx="160" cy="166" rx="104" ry="28" fill="url(#gr-warm)" opacity=".8" />

      {/* the hut: hollow block, tin roof, one signboard */}
      <path d="M52 46h216l-12 16H64z" fill={NIGHT} />
      <rect x="64" y="62" width="192" height="100" fill="#0c1119" />
      <rect x="64" y="62" width="192" height="100" fill="none" stroke="rgba(232,217,176,.08)" />
      <rect x="112" y="70" width="96" height="18" rx="2" fill={DARK} stroke="currentColor" strokeOpacity=".7" />
      <g fill="currentColor" opacity=".8">
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <rect key={i} x={120 + i * 13} y="77" width="9" height="4" rx="1" />
        ))}
      </g>

      {/* desk lamp: one hard pool on the desk, nothing else lit */}
      <rect x="196" y="112" width="4" height="20" fill="#2a3242" />
      <path d="M188 108h20l-5 6h-10z" fill="#39445a" />
      <circle cx="198" cy="116" r="10" fill="url(#gr-warm)" />

      {/* tanod desk */}
      <rect x="106" y="130" width="108" height="10" rx="3" fill="url(#gr-wood)" />
      <rect x="112" y="140" width="6" height="26" fill={NIGHT} />
      <rect x="202" y="140" width="6" height="26" fill={NIGHT} />

      {/* two chairs, facing each other across it */}
      {[
        [78, 1],
        [222, -1],
      ].map(([x, dir]) => (
        <g key={x} fill={NIGHT}>
          <rect x={(x as number) - 12} y="128" width="26" height="6" rx="3" />
          <rect x={(x as number) + ((dir as number) > 0 ? -12 : 6)} y="104" width="8" height="26" rx="3" />
          <rect x={(x as number) - 10} y="134" width="4" height="26" />
          <rect x={(x as number) + 8} y="134" width="4" height="26" />
        </g>
      ))}

      {/* radio on the desk, crackling */}
      <rect x="120" y="120" width="20" height="10" rx="2" fill="#2b3341" />
      <line x1="138" y1="120" x2="146" y2="106" stroke="rgba(232,217,176,.35)" strokeWidth="1" />
      <circle cx="124" cy="125" r="2" fill="currentColor" />
    </svg>
  );
}
